import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  filterOutcomes,
  disciplineTabs,
  scoreBadgeColor,
  requiredContext,
  outcomePath,
  type PublicOutcomeSummary,
} from '../outcomeShowcase';
import { composerContinuePath } from '../composerGuide';

export default function OutcomeShowcase() {
  const [outcomes, setOutcomes] = useState<PublicOutcomeSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeDiscipline, setActiveDiscipline] = useState<string>('All');
  const [minScore, setMinScore] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/outcomes/public')
      .then((res) => {
        if (!res.ok) throw new Error('failed to load outcomes');
        return res.json();
      })
      .then((data) => {
        setOutcomes(Array.isArray(data) ? data : []);
        setLoadError(null);
      })
      .catch(() => {
        // Never substitute sample outcomes here. This grid is the evidence that
        // an outcome was trained and scored, so inventing entries when the
        // registry is unreachable would advertise scores nothing produced.
        setOutcomes([]);
        setLoadError('Could not reach the outcome registry.');
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = filterOutcomes(outcomes, {
    discipline: activeDiscipline,
    minScore: minScore > 0 ? minScore : undefined,
    query: searchQuery,
  });

  const disciplines = disciplineTabs(outcomes);

  return (
    <section className="py-16 px-6 max-w-7xl mx-auto border-t border-border/40" id="showcase">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
        <div>
          <span className="text-xs uppercase tracking-widest text-accent font-mono font-semibold">
            Scored examples
          </span>
          <h2 className="text-3xl font-bold tracking-tight text-textp mt-1">
            Artifacts that already hold a score
          </h2>
          <p className="text-texts text-sm mt-1 max-w-xl">
            Optional proof. Start from the homepage prompt. These listings show a published score.
          </p>
        </div>

        {/* Filter controls */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <input
              type="text"
              placeholder="Search examples..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-surface/80 border border-border text-xs rounded-lg px-3 py-2 pl-8 text-textp focus:outline-none focus:border-accent w-48 md:w-56"
            />
            <i className="ri-search-line absolute left-2.5 top-2.5 text-texts text-xs" />
          </div>

          <select
            value={minScore}
            onChange={(e) => setMinScore(Number(e.target.value))}
            className="bg-surface/80 border border-border text-xs rounded-lg px-2.5 py-2 text-textp focus:outline-none focus:border-accent"
          >
            <option value={0}>All Scores</option>
            <option value={8}>Score ≥ 8.0</option>
            <option value={10}>Certified (≥ 10.0)</option>
          </select>
        </div>
      </div>

      {/* Discipline chips */}
      <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-6 no-scrollbar">
        {disciplines.map((d) => (
          <button
            key={d}
            onClick={() => setActiveDiscipline(d)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
              activeDiscipline === d
                ? 'bg-accent text-accent-text shadow-sm'
                : 'bg-surface/60 text-texts hover:text-textp border border-border/60'
            }`}
          >
            {d}
          </button>
        ))}
      </div>

      {/* Outcome Cards Grid */}
      {loading ? (
        <div className="py-16 text-center text-texts text-sm">Loading examples...</div>
      ) : loadError ? (
        <div className="py-16 text-center border border-dashed border-border rounded-xl bg-surface/20">
          <i className="ri-cloud-off-line text-3xl text-texts mb-2 block" />
          <p className="text-textp font-medium text-sm">{loadError}</p>
          <p className="text-texts text-xs mt-1">Examples will appear here once the registry responds.</p>
        </div>
      ) : outcomes.length === 0 ? (
        <div className="py-16 text-center border border-dashed border-border rounded-xl bg-surface/20">
          <i className="ri-seedling-line text-3xl text-texts mb-2 block" />
          <p className="text-textp font-medium text-sm">No apps have been published yet</p>
          <p className="text-texts text-xs mt-1">
            An example appears here once someone publishes a scored artifact.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center border border-dashed border-border rounded-xl bg-surface/20">
          <i className="ri-inbox-line text-3xl text-texts mb-2 block" />
          <p className="text-textp font-medium text-sm">No outcomes match the current filter</p>
          <p className="text-texts text-xs mt-1">Try lowering the minimum score or clearing search terms.</p>
          <button
            onClick={() => {
              setActiveDiscipline('All');
              setMinScore(0);
              setSearchQuery('');
            }}
            className="mt-4 text-xs font-medium text-accent hover:underline"
          >
            Reset Filters
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((outcome) => {
            const scoreVal = outcome.score?.overall ?? outcome.quality_score ?? 0;
            const badgeTone = scoreBadgeColor(scoreVal);
            const ctxCount = requiredContext(outcome).length;
            const epochs = outcome.test_rig?.epochs;
            const startPrompt = outcome.plain_summary || outcome.description || outcome.title;
            return (
              <div
                key={outcome.id}
                className="bg-surface/40 hover:bg-surface/70 border border-border rounded-xl p-5 flex flex-col justify-between transition-all duration-200 group"
              >
                <div>
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-bg text-texts border border-border/60">
                      {outcome.discipline || 'General'}
                    </span>
                    <span
                      className={`text-[11px] font-mono font-bold px-2 py-0.5 rounded ${
                        badgeTone === 'emerald'
                          ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                          : badgeTone === 'amber'
                          ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                          : 'bg-slate-500/10 text-slate-400 border border-slate-500/20'
                      }`}
                    >
                      ★ {scoreVal.toFixed(1)}/12
                    </span>
                  </div>

                  <Link
                    to={outcomePath(outcome)}
                    className="text-base font-semibold text-textp group-hover:text-accent transition-colors line-clamp-1"
                  >
                    {outcome.title}
                  </Link>
                  <p className="text-xs text-texts mt-1.5 line-clamp-2 min-h-[32px]">
                    {outcome.plain_summary || outcome.description || outcome.playbook?.overview || 'No description recorded.'}
                  </p>
                  <p className="text-[11px] text-texts mt-2 font-mono">
                    {ctxCount > 0 ? `${ctxCount} context ${ctxCount === 1 ? 'item' : 'items'} required` : 'No extra context listed'}
                    {epochs ? ` · ${epochs} epoch${epochs === 1 ? '' : 's'} trained` : ''}
                  </p>
                </div>

                <div className="mt-5 pt-3 border-t border-border/40 flex items-center justify-between">
                  <Link
                    to={outcomePath(outcome)}
                    className="text-xs text-texts hover:text-textp flex items-center gap-1 font-medium transition-colors"
                  >
                    <i className="ri-shield-check-line text-accent" /> View proof
                  </Link>
                  <a
                    href={composerContinuePath(startPrompt)}
                    className="text-xs bg-accent/10 hover:bg-accent text-accent hover:text-accent-text border border-accent/20 px-3 py-1.5 rounded-md font-medium transition-all flex items-center gap-1.5"
                  >
                    Start from this
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
